import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import HomePage from './pages/HomePage';
import ContactPage from './pages/ContactPage';
import ImpressumPage from './pages/ImpressumPage';
import PrivacyPage from './pages/PrivacyPage';
import AGBPage from './pages/AGBPage';
import LoginPage from './pages/LoginPage';
import PreviewPage from './pages/PreviewPage';
import PreviewEditPage from './pages/PreviewEditPage';
import PrivatePage from './pages/PrivatePage';
import ArbeitsZeit from './pages/Arbeitszeit';
import AdminPanel from './pages/AdminPanel';
import QRCodeGenerator from './pages/QRCode';
import Ausgaben from './pages/Ausgaben';
import Kontakt from './pages/Kontakt';
import NotFoundPage from './pages/NotFoundPage';
import Essentials from './pages/Essentials';
import Akquise from './pages/Akquise';
import Templates from './pages/Templates';
import Rechner from './pages/Rechner';
import Installer from './pages/Installer';
import FloatingAdminChat from './components/FloatingAdminChat';

const API = process.env.REACT_APP_API_URL;

function VisitTracker() {
    const location = useLocation();
    const { user, loading } = useAuth();
    useEffect(() => {
        if (loading) return;
        if (user?.role === 'admin') return;
        const key = location.pathname;
        const tracked = JSON.parse(sessionStorage.getItem('tracked_paths') || '[]');
        if (tracked.includes(key)) return;
        sessionStorage.setItem('tracked_paths', JSON.stringify([...tracked, key]));
        const area = location.pathname.startsWith('/privat') ? 'private' : 'public';
        fetch(`${API}/api/visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: location.pathname, area, username: user?.username || null })
        }).catch(() => { });
    }, [location.pathname, user, loading]);

    return null;
}

function SiteLock({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [locked, setLocked] = useState(true);

    const handleDecoClick = () => {
        navigate(user ? '/privat' : '/login');
    };

    useEffect(() => {
        fetch(`${API}/api/site-status`)
            .then(r => r.json())
            .then(d => setLocked(d.locked))
            .catch(() => {}); // fail closed: keep locked=true if the check fails
    }, []);

    const legalPaths = ['/impressum', '/agb', '/privacy'];
    const bypass = location.pathname === '/login' || location.pathname === '/preview' || location.pathname === '/preview-edit' || location.pathname.startsWith('/privat') || legalPaths.includes(location.pathname);
    const isAdmin = user?.role === 'admin';

    if (!loading && locked && !isAdmin && !bypass) {
        return (
            <div className="hero-v3" style={{
                minHeight: 'calc(100vh - 140px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', textAlign: 'center', padding: '40px'
            }}>
                <div className="hero-v3-grid" />
                <div className="hero-grain" />

                {/* Versteckter Zugang — kein cursor pointer, kein Hinweis */}
                <div
                    className="hero-v3-deco"
                    aria-hidden="true"
                    onClick={handleDecoClick}
                    style={{ cursor: 'default' }}
                >
                    &lt;/&gt;
                </div>
                <h1 style={{ fontSize: '36px', fontWeight: 800, marginBottom: '16px', position: 'relative', zIndex: 1 }}>Bald verfügbar</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '400px', lineHeight: 1.6, position: 'relative', zIndex: 1 }}>
                    Diese Seite befindet sich gerade im Aufbau. Schau bald wieder vorbei!
                </p>
            </div>
        );
    }

    return children;
}

function AppInner() {
    // /preview ist ein Testwerkzeug für Kundenseiten (eigenes iframe-Fenster) —
    // unser eigener Seiten-Header/Footer ("die schwarzen Balken") würde da nur stören.
    const location = useLocation();
    const bareChrome = location.pathname === '/preview';

    return (
        <div className="App">
            <VisitTracker />
            {!bareChrome && <Header />}
            <main>
                <SiteLock>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/impressum" element={<ImpressumPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/agb" element={<AGBPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/preview" element={<PreviewPage />} />
                    <Route path="/preview-edit" element={<PreviewEditPage />} />
                    <Route path="/privat" element={<PrivateRoute><PrivatePage /></PrivateRoute>} />
                    <Route path="/privat/arbeitszeit" element={<PrivateRoute permission="arbeitszeit"><ArbeitsZeit /></PrivateRoute>} />
                    <Route path="/privat/admin" element={<PrivateRoute permission="admin"><AdminPanel /></PrivateRoute>} />
                    <Route path="/privat/qrcode" element={<PrivateRoute permission="qrcode"><QRCodeGenerator /></PrivateRoute>} />
                    <Route path="/privat/ausgaben" element={<PrivateRoute permission="ausgaben"><Ausgaben /></PrivateRoute>} />
                    <Route path="/privat/kontakt" element={<PrivateRoute permission="kontakt"><Kontakt /></PrivateRoute>} />
                    <Route path="/privat/essentials" element={<PrivateRoute permission="essentials"><Essentials /></PrivateRoute>} />
                    <Route path="/privat/akquise" element={<PrivateRoute permission="akquise"><Akquise /></PrivateRoute>} />
                    <Route path="/privat/templates" element={<PrivateRoute permission="templates"><Templates /></PrivateRoute>} />
                    <Route path="/privat/rechner" element={<PrivateRoute permission="rechner"><Rechner /></PrivateRoute>} />
                    <Route path="/privat/exe" element={<PrivateRoute permission="installer"><Installer /></PrivateRoute>} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </SiteLock>
            </main>
            {!bareChrome && <Footer />}
            <FloatingAdminChat />
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <AppInner />
                    </BrowserRouter>
                </AuthProvider>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;